<template>
  <v-dialog v-model="orderDocumentDialog" max-width="380">
    <v-card class="pdf-dialog" rounded="lg">
      <v-card-text class="pdf-dialog__body">
        <!-- State icon -->
        <div :class="['pdf-dialog__icon', `pdf-dialog__icon--${state}`]">
          <v-progress-circular v-if="state === 'working'" color="primary" indeterminate size="28" width="3"/>
          <v-icon v-else-if="state === 'error'" color="error" size="28">mdi-alert-circle-outline</v-icon>
          <v-icon v-else color="success" size="28">mdi-check</v-icon>
        </div>

        <div class="pdf-dialog__title">{{ title }}</div>
        <div class="pdf-dialog__caption">{{ caption }}</div>
      </v-card-text>

      <v-card-actions class="pdf-dialog__actions">
        <template v-if="state === 'error'">
          <v-btn variant="text" @click="orderDocumentDialog = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" prepend-icon="mdi-refresh" variant="flat" @click="retry()">Try again</v-btn>
        </template>

        <template v-else-if="state === 'done'">
          <v-btn variant="text" @click="orderDocumentDialog = false">Close</v-btn>
          <v-btn
            v-if="mode === 'download'"
            :download="savedFilename"
            :href="fileUrl"
            color="primary"
            prepend-icon="mdi-download"
            variant="tonal"
          >
            Save again
          </v-btn>
          <v-btn
            v-else
            :href="fileUrl"
            color="primary"
            prepend-icon="mdi-open-in-new"
            target="_blank"
            variant="tonal"
          >
            Open PDF
          </v-btn>
        </template>

        <v-btn v-else variant="text" @click="orderDocumentDialog = false">{{ $t('cancel') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script lang="ts" setup>
import {computed, ref, watch} from 'vue';
import {useStore} from 'vuex';
import {isAxiosError} from 'axios';

type DialogMode = 'download' | 'print';

// Reactive state
const mode = ref<DialogMode>('download');
const popupBlocked = ref(false);
const savedFilename = ref('');
const savedObjectUrl = ref<string | null>(null);
let preOpenedWindow: Window | null = null;
const errorMessage = ref<string | null>(null);
const orderDocumentDialog = ref(false);
const currentWorkingOrderDocumentUuid = ref<string | null>(null);

// Vuex store
const store = useStore();

// Computed properties
const downloadStatus = computed(() => store.getters['order/downloadStatus']);

const fileUrl = computed<string | undefined>(() => savedObjectUrl.value ?? downloadStatus.value?.url);

type DialogState = 'working' | 'done' | 'error';
const state = computed<DialogState>(() => {
  if (errorMessage.value) {
    return 'error';
  }
  return fileDone.value ? 'done' : 'working';
});

const title = computed(() => {
  if (state.value === 'error') {
    return mode.value === 'print' ? 'Couldn\'t open the PDF' : 'Couldn\'t download the PDF';
  }
  if (state.value === 'working') {
    return 'Preparing your PDF';
  }
  return mode.value === 'print' ? 'Opened in a new tab' : 'Downloaded';
});

const caption = computed(() => {
  if (state.value === 'error') {
    return errorMessage.value ?? '';
  }
  if (state.value === 'working') {
    return 'Rendering the latest version of this document.';
  }
  if (mode.value === 'print') {
    return popupBlocked.value
      ? 'Your browser blocked the tab. Use the button below to open it.'
      : 'Use your browser\'s print command from that tab.';
  }
  return savedFilename.value ? `${savedFilename.value} is in your downloads.` : 'The file is in your downloads.';
});
const fileDone = computed<boolean>(() => Boolean(savedObjectUrl.value) || Boolean(downloadStatus.value?.done && downloadStatus.value?.url));

// Actions
const saveFile = async (documentUuid: string) => {
  errorMessage.value = null;
  releaseObjectUrl();

  try {
    const {blob, filename} = await store.dispatch('order/downloadOrderDocumentFile', {documentUuid, regenerate: true});
    const url = URL.createObjectURL(blob);

    savedFilename.value = filename;
    savedObjectUrl.value = url;

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    errorMessage.value = isAxiosError(error) && error.response?.data?.message
      ? String(error.response.data.message)
      : 'The PDF could not be generated. Please try again.';
  }
};

const releaseObjectUrl = (): void => {
  if (savedObjectUrl.value) {
    URL.revokeObjectURL(savedObjectUrl.value);
  }
  savedObjectUrl.value = null;
  savedFilename.value = '';
};

const downloadOrderDocument = async (documentUuid: string) => {
  errorMessage.value = null;
  store.commit('order/SET_ORDER_DOCUMENT_DOWNLOAD_STATUS', null);

  try {
    // The PDF is rendered on the server in this request; always ask for a
    // fresh render so edits since the last download are reflected.
    const status = await store.dispatch('order/downloadOrderDocument', {documentUuid, regenerate: true});

    if (!status?.url) {
      errorMessage.value = 'The PDF could not be generated. Please try again.';
      closePreOpenedWindow();
    }
  } catch (error) {
    errorMessage.value = isAxiosError(error) && error.response?.data?.message
      ? String(error.response.data.message)
      : 'The PDF could not be generated. Please try again.';
    closePreOpenedWindow();
  }
};

const openDownloadUrl = (url: string): void => {
  // A tab opened synchronously in the click handler survives pop-up
  // blockers; point it at the PDF now that the render has finished.
  if (preOpenedWindow && !preOpenedWindow.closed) {
    preOpenedWindow.location.href = url;
    preOpenedWindow.focus();
    preOpenedWindow = null;
    popupBlocked.value = false;

    return;
  }

  const popup = window.open(url, '_blank');

  if (popup) {
    popup.focus();
    popupBlocked.value = false;

    return;
  }

  const link = document.createElement('a');
  link.href = url;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.click();
  popupBlocked.value = true;
};

const closePreOpenedWindow = (): void => {
  if (preOpenedWindow && !preOpenedWindow.closed) {
    preOpenedWindow.close();
  }
  preOpenedWindow = null;
};

const start = (documentUuid: string): void => {
  if (mode.value === 'download') {
    // Download saves the file straight to disk: no tab, no viewer.
    void saveFile(documentUuid);
    return;
  }

  // Print opens the PDF in a tab for the browser's print dialog. The tab must
  // be opened synchronously inside the user's click for pop-up blockers.
  preOpenedWindow = window.open('', '_blank');
  void downloadOrderDocument(documentUuid);
};

const retry = () => {
  if (currentWorkingOrderDocumentUuid.value) {
    start(currentWorkingOrderDocumentUuid.value);
  }
};

// Event handlers
const handleOpenDownloadDialog = (document_uuid: string, dialogMode: DialogMode = 'download') => {
  mode.value = dialogMode;
  orderDocumentDialog.value = true;
  currentWorkingOrderDocumentUuid.value = document_uuid;
  start(document_uuid);
};

// Watchers
watch(fileDone, (status) => {
  if (status && fileUrl.value && mode.value === 'print') {
    openDownloadUrl(fileUrl.value);
  }
});

watch(orderDocumentDialog, (status) => {
  if (!status) {
    currentWorkingOrderDocumentUuid.value = null;
    popupBlocked.value = false;
    errorMessage.value = null;
    closePreOpenedWindow();
    releaseObjectUrl();
    store.commit('order/SET_ORDER_DOCUMENT_DOWNLOAD_STATUS', null);
  }
});

defineExpose({openDialog: handleOpenDownloadDialog});
</script>

<style scoped>
.pdf-dialog__body {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 28px 24px 8px;
}

.pdf-dialog__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  margin-bottom: 14px;
  background: rgba(var(--v-theme-primary), 0.08);
}

.pdf-dialog__icon--done {
  background: rgba(var(--v-theme-success), 0.12);
}

.pdf-dialog__icon--error {
  background: rgba(var(--v-theme-error), 0.12);
}

.pdf-dialog__title {
  font-size: 1.05rem;
  font-weight: 600;
  line-height: 1.3;
  color: rgb(var(--v-theme-on-surface));
}

.pdf-dialog__caption {
  margin-top: 6px;
  font-size: 0.875rem;
  line-height: 1.4;
  color: rgba(var(--v-theme-on-surface), 0.64);
  max-width: 30ch;
}

.pdf-dialog__actions {
  justify-content: center;
  gap: 8px;
  padding: 8px 24px 20px;
}
</style>
